<template>
  <div class="pdv-address-validation">
    <div
      v-if="
        initialized &&
        currentPdvState !== PDV_STATES.FAILED &&
        !displayFailedMsg
      "
    >
      <Icon :icon="modeSettings.icon" />
      <h1>{{ $t(modeSettings.heading) }}</h1>

      <div v-if="softCheckSucceeded" class="verified-msg">
        <p v-if="manualAddressEdit">{{ $t('PDV.VERIFIED.EDIT') }}</p>
        <p v-else>{{ $t('PDV.VERIFIED.INFO') }}</p>
      </div>
      <div
        v-if="
          (![PDV_STATES.CODE_INPUT].includes(currentPdvState) &&
            !softCheckSucceeded) ||
          manualAddressEdit
        "
        class="validate-msg"
      >
        <p v-if="!softCheckSucceeded" class="subheading">
          {{ $t('ONBOARDING.ADDRESS_DIALOG.SUBHEADING') }}
        </p>
        <div class="attempt-info">
          <p>{{ $t('PDV.VALIDATE_INFO') }}</p>
          <p
            v-if="pdvState.softcheck.attemptsLeft < 5"
            class="attempts"
            :class="{ highlight: softCheckFailed }"
          >
            {{
              $t('PDV.ATTEMPTS_LEFT', {
                count: pdvState.softcheck.attemptsLeft,
              })
            }}
          </p>
        </div>
      </div>

      <PersonalDataForm
        class="address-form"
        :update-pdv-state="fetchPdvState"
        hide-pdv-validate-msg
        skip-pdv-failed-dialog
        @on-edit-address="onEditAddress"
      />

      <div v-if="mode === 'PUBLISH' && !termsAlreadyAccepted" class="tnc">
        <Checkbox id="terms" v-model="termsAccepted" />
        <label>
          <span>{{ $t('SHOPS.ADDRESS_DIALOG.TERMS.INTRO') }}</span>
          <a class="link link-main" :href="TcLinkTarget" target="_blank">{{
            $t('SHOPS.ADDRESS_DIALOG.TERMS.SHOP_TERMS_LINK')
          }}</a
          >.
        </label>
      </div>

      <ProgressButton
        class="btn btn-primary btn-block btn-lg pdv-save-btn"
        :label="$t('GENERAL.DONE')"
        :disabled="saveBtnDisabled"
        :on-click="save"
      />
      <a
        v-if="softCheckFailed"
        href="#"
        class="link link-main contact-link"
        @click.prevent="setFailedMsgDisplay(true)"
        >{{ $t('PDV.CONTACT') }}</a
      >
    </div>
    <div
      v-else-if="
        initialized &&
        (currentPdvState === PDV_STATES.FAILED || displayFailedMsg)
      "
      class="failed-address"
    >
      <PdvFailedMsg :address="serverSavedAddress" />
      <a
        v-if="displayFailedMsg"
        href="#"
        class="link link-main icon-link back-link"
        @click.prevent="setFailedMsgDisplay(false)"
      >
        <Icon icon="left" /> {{ $t('PDV.BACK') }}
      </a>
      <button
        v-if="!displayFailedMsg && mode === 'CONFIRM'"
        class="bbtn btn-primary btn-block btn-lg pdv-save-btn"
        @click="$emit('onDone')"
      >
        {{ $t('PDV.CONFIRMATION.CONTINUE') }}
      </button>
    </div>
    <div v-else class="loading-wrapper">
      <LoadingHeart />
    </div>
    <div v-if="waitingForTcRequest" class="loading-overlay">
      <LoadingHeart />
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import { mapState as mapPiniaState, mapActions } from 'pinia';
import PersonalDataForm from 'src/app/partnerarea/account/personal/PersonalDataForm.vue';
import pdvService from 'commons/partnerDataValidation/service/pdvService';
import ProgressButton from 'commons/btnProgress/ProgressButton.vue';
import PdvFailedMsg from 'commons/partnerDataValidation/failedMsg/PdvFailedMsg.vue';
import LoadingHeart from 'commons/LoadingHeart/LoadingHeart.vue';
import { getExternalLinks } from 'commons/externalLinks/externalLinks';
import Checkbox from 'commons/checkbox/Checkbox.vue';
import analyticsService from 'commons/tracking/analytics';
import { useUserAddress } from 'commons/stores/user/userAddress';

const MODE_SETTINGS = {
  PUBLISH: {
    icon: 'rocket',
    heading: 'ONBOARDING.ADDRESS_DIALOG.HEADING',
  },
  CONFIRM: {
    icon: 'lock',
    heading: 'PDV.CONFIRMATION.HEADING',
  },
};

export default {
  name: 'PdvAddressValidation',
  components: {
    PersonalDataForm,
    ProgressButton,
    PdvFailedMsg,
    LoadingHeart,
    Checkbox,
  },
  props: {
    mode: { type: String, default: 'PUBLISH' },
  },
  emits: ['onDone'],
  data() {
    return {
      manualAddressEdit: false,
      PDV_STATES: pdvService.STATES,
      initialized: false,
      displayFailedMsg: false,
      TcLinkTarget: getExternalLinks().SHOP_TERMS,
      termsAccepted: null,
      waitingForTcRequest: false,
    };
  },
  computed: {
    ...mapPiniaState(useUserAddress, {
      pdvState: 'pdvState',
      serverSavedAddress: 'originalData',
    }),
    ...mapState({
      termsAlreadyAccepted: (state) => state.user.data.acceptTermsAndConditions,
    }),
    modeSettings() {
      return MODE_SETTINGS[this.mode] || MODE_SETTINGS.PUBLISH;
    },
    currentPdvState() {
      return pdvService.getCurrentState(this.pdvState);
    },
    softCheckSucceeded() {
      return pdvService.softCheckSucceeded(this.pdvState);
    },
    softCheckFailed() {
      return pdvService.softCheckFailed(this.pdvState);
    },
    saveBtnDisabled() {
      return this.mode === 'PUBLISH' && !this.termsAccepted;
    },
  },
  async created() {
    this.termsAccepted = this.termsAlreadyAccepted;

    if (
      ![
        this.PDV_STATES.VALID,
        this.PDV_STATES.CODE_INPUT,
        this.PDV_STATES.CONFIRM_NEEDED,
      ].includes(this.currentPdvState)
    ) {
      this.manualAddressEdit = true;
    }

    this.initialized = true;
    analyticsService.logEvent('pdv_start');
  },
  methods: {
    ...mapActions(useUserAddress, ['fetchPdvState', 'saveAddressData']),
    ...mapMutations({
      updateUserData: 'user/updateUserData',
    }),
    onEditAddress() {
      this.manualAddressEdit = true;
    },
    setFailedMsgDisplay(state) {
      this.displayFailedMsg = state;
    },
    save() {
      try {
        return this.saveAddressData({
          forceValidation: true,
        })
          .then(
            async (result) => {
              if (pdvService.softCheckFailed(this.pdvState)) {
                this.termsAccepted = this.termsAlreadyAccepted;
                return Promise.reject(result.origin);
              } else {
                if (this.mode === 'PUBLISH') {
                  this.waitingForTcRequest = true;
                  await pdvService.acceptTermsAndConditions().catch((e) => {
                    this.waitingForTcRequest = false;
                    throw e;
                  });

                  this.waitingForTcRequest = false;

                  this.updateUserData({
                    acceptTermsAndConditions: true,
                  });

                  this.$emit('onDone');
                } else {
                  this.$emit('onDone');
                }

                analyticsService.logEvent('pdv_success');
                return Promise.resolve(result.origin);
              }
            },
            (error) => {
              analyticsService.logEvent('pdv_fail');
              return Promise.reject(error);
            },
          )
          .catch((error) => {
            analyticsService.logEvent('pdv_fail');
            return Promise.reject(error);
          });
      } catch (e) {
        console.error(e);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';
@import 'src/scss/styleguide/type';

.pdv-address-validation {
  max-width: 600px;
  position: relative;
}

.icon {
  color: $pa-color-main;
  width: 48px;
  height: 48px;
}

h1 {
  margin: 18px 0 12px 0;
}

.validate-msg {
  p {
    @extend .text-sm;
  }
}

.attempt-info {
  p {
    display: inline;
  }

  .attempts.highlight {
    color: $pa-color-red;
  }
}

.address-form {
  margin-top: 18px;
}

.tnc {
  display: flex;
  align-items: center;
  margin-top: 24px;
}

.pdv-save-btn {
  margin-top: 24px;
  height: 48px;
}

.contact-link {
  @extend .text-sm;
  display: inline-block;
  margin-top: 12px;
}

.back-link {
  margin-top: 12px;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 2;
}
</style>
